import { Box, Container } from '@mui/material'
import React, { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'

import { useAppSelector } from '@/hooks/redux'
import { useGetRecipesQuery } from '@/store/apiService'
import {
  clearSelectedCases,
  clearTrashSelectedCases,
  setStatusFiltersForTrash,
  toggleTrashSelectedRow,
} from '@/store/project'

import { Project, ProjectStatus } from '..'
import ProjectsTable from '../ProjectTable'
import TrashActionMenu from './TrashActionMenu'
import TrashHeader from './TrashHeader'

function Trash() {
  const [tabValue] = useState<number>(0)
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [menuRowIndex, setMenuRowIndex] = useState<number | null>(null)

  const dispatch = useDispatch()
  const statusFilters = useAppSelector(
    (state) => state.project.statusFilterForTrash,
  )
  const selectedRows = useAppSelector(
    (state) => state.project.setTrashSelectedRows,
  )
  const [offset, setOffset] = useState(0)
  const [ordering, setOrdering] = useState<string>()
  const [projects, setProjects] = useState<Project[]>([])

  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')

  const limit = 10

  useEffect(() => {
    setOffset(0)
  }, [tabValue, statusFilters, ordering])

  useEffect(() => {
    const timeout = setTimeout(() => {
      setDebouncedSearch(search)
    }, 300)
    return () => clearTimeout(timeout)
  }, [search])

  const { data: recipeData, refetch: refetchRecipe } = useGetRecipesQuery(
    {
      limit,
      offset,
      status: statusFilters?.join(','),
      deleted_cases: true,
      shared: false,
      ordering,
      search: debouncedSearch,
    },
    {
      refetchOnMountOrArgChange: true,
    },
  )

  useEffect(() => {
    if (recipeData?.results) {
      setProjects(recipeData?.results)
    }
  }, [recipeData, tabValue, statusFilters])

  const handleCheckboxToggle = (id: string, name: string) => {
    dispatch(toggleTrashSelectedRow({ id, name }))
  }

  const handleStatusChange = (index: number, newStatus: ProjectStatus) => {
    const updated = [...projects]
    updated[index].status = newStatus
    setProjects(updated)
  }

  const handleMenuOpen = (
    event: React.MouseEvent<HTMLElement>,
    index: number,
  ) => {
    setAnchorEl(event.currentTarget)
    setMenuRowIndex(index)
  }

  const handleMenuClose = () => {
    setAnchorEl(null)
    setMenuRowIndex(null)
  }

  const handlePrev = () => {
    dispatch(clearSelectedCases())
    dispatch(clearTrashSelectedCases())
    setOffset((prev) => Math.max(prev - limit, 0))
  }

  const handleNext = () => {
    dispatch(clearSelectedCases())
    dispatch(clearTrashSelectedCases())
    setOffset((prev) => prev + limit)
  }

  return (
    <Box overflow="auto" flex={1} sx={{ my: '32px' }}>
      <Container maxWidth="lg" sx={{ mt: 5 }}>
        <TrashHeader
          selectedRows={selectedRows}
          refetch={refetchRecipe}
          search={search}
          setSearch={setSearch}
        />
        <ProjectsTable
          projects={projects}
          handleStatusChange={handleStatusChange}
          handleMenuOpen={handleMenuOpen}
          tabValue={tabValue}
          selectedRows={selectedRows}
          handleCheckboxToggle={handleCheckboxToggle}
          setStatusFilters={(filters) =>
            dispatch(setStatusFiltersForTrash(filters))
          }
          statusFilters={statusFilters}
          handlePrev={handlePrev}
          handleNext={handleNext}
          page={Math.floor(offset / limit)}
          rowsPerPage={limit}
          count={recipeData?.count || 0}
          ordering={ordering}
          setOrdering={setOrdering}
          refetch={refetchRecipe}
        />
        <TrashActionMenu
          anchorEl={anchorEl}
          handleMenuClose={handleMenuClose}
          caseData={menuRowIndex !== null ? projects[menuRowIndex] : null}
          refetch={refetchRecipe}
        />
      </Container>
    </Box>
  )
}

export default Trash
